import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import './BrandPage.css';

interface BrandConfig {
  name: string;
  displayName: string;
  colors: {
    primary: string;
    secondary: string;
    text: string;
  };
  gradients: {
    background: string;
    card: string;
  };
  description: string;
  fullDescription: string;
  logo: string;
  features: string[];
  gallery: string[];
}

const brandConfigs: Record<string, BrandConfig> = {
  elvang: {
    name: 'elvang',
    displayName: 'Elvang',
    colors: {
      primary: '#c2ab8a',
      secondary: '#d6c3a9',
      text: '#706c6c'
    },
    gradients: {
      background: 'linear-gradient(135deg, #c2ab8a 0%, #d6c3a9 100%)',
      card: 'linear-gradient(135deg, rgba(194, 171, 138, 0.9) 0%, rgba(214, 195, 169, 0.9) 100%)'
    },
    description: 'Luxury textiles and premium materials',
    fullDescription: 'Elvang creates beautiful, sustainable textiles with a focus on quality and craftsmanship. From luxurious throws to premium home accessories, each piece is designed to bring comfort and elegance to your living space.',
    logo: '/logos/elvang.png',
    features: ['Sustainable Materials', 'Premium Quality', 'Danish Design', 'Eco-Friendly'],
    gallery: ['/logos/elvang2.png', '/logos/elvang.jpeg']
  },
  gefu: {
    name: 'gefu',
    displayName: 'Gefu',
    colors: {
      primary: '#a39d9d',
      secondary: '#6e6e6e',
      text: '#f2efef'
    },
    gradients: {
      background: 'linear-gradient(135deg, #a39d9d 0%, #6e6e6e 100%)',
      card: 'linear-gradient(135deg, rgba(163, 157, 157, 0.9) 0%, rgba(110, 110, 110, 0.9) 100%)'
    },
    description: 'Innovative kitchen solutions',
    fullDescription: 'Gefu revolutionizes the kitchen experience with innovative tools and gadgets that make cooking more efficient and enjoyable. German engineering meets creative design to enhance your culinary adventures.',
    logo: '/logos/gefu.png',
    features: ['German Engineering', 'Kitchen Innovation', 'Premium Tools', 'Functional Design'],
    gallery: ['/logos/gefu.png']
  },
  remember: {
    name: 'remember',
    displayName: 'Remember',
    colors: {
      primary: '#F4D03F',
      secondary: '#F8E49D',
      text: '#8B6914'
    },
    gradients: {
      background: 'linear-gradient(135deg, #F4D03F 0%, #F8E49D 100%)',
      card: 'linear-gradient(135deg, rgba(244, 208, 63, 0.9) 0%, rgba(248, 228, 157, 0.9) 100%)'
    },
    description: 'Memorable moments, beautiful memories',
    fullDescription: 'Remember creates products that capture and celebrate life\'s special moments. From elegant stationery to unique gift items, every piece is designed to help you create lasting memories.',
    logo: '/logos/remember.png',
    features: ['Creative Stationery', 'Gift Items', 'Unique Designs', 'Special Moments'],
    gallery: ['/logos/remember1.png', '/logos/remember.jpeg']
  },
  rader: {
    name: 'rader',
    displayName: 'Rader',
    colors: {
      primary: '#E8D2AE',
      secondary: '#D4B896',
      text: '#8B7355'
    },
    gradients: {
      background: 'linear-gradient(135deg, #E8D2AE 0%, #D4B896 100%)',
      card: 'linear-gradient(135deg, rgba(232, 210, 174, 0.9) 0%, rgba(212, 184, 150, 0.9) 100%)'
    },
    description: 'Contemporary design with timeless appeal',
    fullDescription: 'Rader combines contemporary design with timeless elegance, creating pieces that enhance modern living. Their carefully curated collection brings sophistication and warmth to any space.',
    logo: '/logos/rader.png',
    features: ['Contemporary Design', 'Timeless Appeal', 'Quality Craftsmanship', 'Modern Living'],
    gallery: ['/logos/rader1.png']
  },
  ppd: {
    name: 'ppd',
    displayName: 'PPD',
    colors: {
      primary: '#48CAE4',
      secondary: '#6BD4F0',
      text: '#1B4C5C'
    },
    gradients: {
      background: 'linear-gradient(135deg, #48CAE4 0%, #6BD4F0 100%)',
      card: 'linear-gradient(135deg, rgba(72, 202, 228, 0.9) 0%, rgba(107, 212, 240, 0.9) 100%)'
    },
    description: 'Creative paper products',
    fullDescription: 'PPD specializes in creative paper products that inspire and delight. From beautiful napkins to unique gift items, their designs bring color and creativity to everyday moments.',
    logo: '/logos/ppd.png',
    features: ['Creative Designs', 'Paper Products', 'Unique Napkins', 'Gift Items'],
    gallery: ['/logos/ppd.png']
  },
  relaxound: {
    name: 'relaxound',
    displayName: 'Relaxound',
    colors: {
      primary: '#52B788',
      secondary: '#6BC794',
      text: '#1D4332'
    },
    gradients: {
      background: 'linear-gradient(135deg, #52B788 0%, #6BC794 100%)',
      card: 'linear-gradient(135deg, rgba(82, 183, 136, 0.9) 0%, rgba(107, 199, 148, 0.9) 100%)'
    },
    description: 'Sounds of nature, peace of mind',
    fullDescription: 'Relaxound brings the calming sounds of nature into your daily life. Their innovative sound products create peaceful environments that help you relax, focus, and reconnect with nature.',
    logo: '/logos/relaxound.png',
    features: ['Natural Sounds', 'Relaxation', 'Innovative Design', 'Peace of Mind'],
    gallery: ['/logos/relaxound1.png', '/logos/Relaxound.jpg', '/logos/RELV01__1_.jpg']
  },
  myflame: {
    name: 'myflame',
    displayName: 'My Flame Lifestyle',
    colors: {
      primary: '#c76e87',
      secondary: '#c58f9e',
      text: '#605457'
    },
    gradients: {
      background: 'linear-gradient(135deg, #c76e87 0%, #c58f9e 100%)',
      card: 'linear-gradient(135deg, rgba(199, 110, 135, 0.9) 0%, rgba(197, 143, 158, 0.9) 100%)'
    },
    description: 'Candles and lifestyle products',
    fullDescription: 'My Flame Lifestyle creates beautiful candles and lifestyle products that bring warmth and ambiance to your home. Each piece is crafted with care to enhance your living experience.',
    logo: '/logos/myflame.png',
    features: ['Premium Candles', 'Lifestyle Products', 'Home Ambiance', 'Quality Craftsmanship'],
    gallery: ['/logos/myflame1.png', '/logos/myflamer.jpeg']
  }
};

const BrandPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  
  const brand = brandId ? brandConfigs[brandId] : null;

  useEffect(() => {
    if (!brand) {
      navigate('/');
      return;
    }

    setTimeout(() => setIsLoaded(true), 100);

    // Update CSS custom properties for dynamic theming
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', brand.colors.primary);
    root.style.setProperty('--brand-secondary', brand.colors.secondary);
    root.style.setProperty('--brand-text', brand.colors.text);
    root.style.setProperty('--brand-bg-gradient', brand.gradients.background);
    root.style.setProperty('--brand-card-gradient', brand.gradients.card);

    return () => {
      // Reset to default colors when leaving
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-text');
      root.style.removeProperty('--brand-bg-gradient');
      root.style.removeProperty('--brand-card-gradient');
    };
  }, [brand, navigate]);

  if (!brand) {
    return null;
  }

  return (
    <div className={`brand-page ${isLoaded ? 'loaded' : ''}`}>
      <div className="brand-animated-background">
        <div className="brand-bg-gradient-1"></div>
        <div className="brand-bg-gradient-2"></div>
        <div className="brand-bg-gradient-3"></div>
      </div>
      
      <Header />
      
      <main className="brand-main-content">
        <section className="brand-hero-section">
          <div className="brand-hero-content">
            <div className="brand-logo-container">
              <img src={brand.logo} alt={brand.displayName} className="brand-logo-main" />
            </div>
            <h1 className="brand-hero-title">{brand.displayName}</h1>
            <p className="brand-hero-subtitle">{brand.description}</p>
          </div>
        </section>
        
        <section className="brand-content-section">
          <div className="brand-layout">
            <div className="brand-info-card">
              <div className="brand-info-content">
                <h2>About {brand.displayName}</h2>
                <p>{brand.fullDescription}</p>
                
                <div className="brand-features">
                  <h3>Key Features</h3>
                  <div className="features-grid">
                    {brand.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <div className="feature-dot"></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="brand-actions">
                  <button 
                    className="brand-action-btn primary"
                    onClick={() => navigate('/catalogues')}
                  >
                    View Catalogue
                  </button>
                  <button 
                    className="brand-action-btn secondary"
                    onClick={() => navigate('/customer/auth')}
                  >
                    Customer Portal
                  </button>
                </div>
              </div>
            </div>
            
            <div className="brand-gallery-section">
              <h3>Product Gallery</h3>
              <div className="brand-gallery">
                {brand.gallery.map((image, index) => (
                  <div key={index} className="gallery-item">
                    <div className="gallery-image-container">
                      <img src={image} alt={`${brand.displayName} product ${index + 1}`} />
                      <div className="gallery-overlay">
                        <span>View Product</span>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="gallery-placeholder">
                  <div className="placeholder-content">
                    <span>More products coming soon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BrandPage;